/**
 * Per-campaign MONTHLY budgets for the Budgets tab.
 *
 * Pacing normally derives every campaign's daily from the account total:
 * `share x target`. A percentage is a proportion, not a commitment, so it
 * delivers the intended dollars only while the total happens to match — move
 * the contract and every figure silently moves with it.
 *
 * Entering a monthly budget here pins that campaign to its own number. The
 * contracted monthly budget stays the cap, and the save is refused if the
 * budgets exceed it: campaign budgets no longer derive from that figure, so
 * no later check would catch an overrun.
 *
 * Deliberately a separate card rather than another mode inside the pacing
 * preview table, which already edits share and proposed-daily. Two ways to
 * express the same number in one grid is how an operator ends up unsure which
 * one is winning.
 *
 * This card never spends. It writes the budgets; the Mon/Thu pacing job reads
 * them.
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2, PiggyBank, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useMutation } from '@tanstack/react-query';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  updateCampaignBudgets,
  type CampaignMonthlyBudget,
} from '@/services/googleAdsMonthlyService';

export interface CampaignBudgetsCardProps {
  tenantId: string;
  /** Campaigns currently in pacing scope — id and display name. */
  campaigns: Array<{ campaign_id: string; campaign_name: string }>;
  /** Contracted monthly Google Ads budget. The cap the total is checked against. */
  contractedMonthlyUsd?: number | null;
  /** Budgets already saved for this client, keyed by campaign id. */
  saved?: Record<string, CampaignMonthlyBudget>;
  onSaved?: () => void;
}

const usd = (n: number) =>
  n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  });

/** Blank means "not on a fixed budget" and must stay distinguishable from 0,
 *  which means "budget this campaign at nothing". */
type Draft = Record<string, string>;

export function draftFromSaved(
  saved: Record<string, CampaignMonthlyBudget> | undefined,
  campaigns: Array<{ campaign_id: string }>,
): Draft {
  const out: Draft = {};
  for (const c of campaigns) {
    const hit = saved?.[c.campaign_id];
    out[c.campaign_id] = hit ? String(hit.monthly) : '';
  }
  return out;
}

/** Sum of the entries that parse. A half-typed row must not read as $0 and
 *  make the total lurch while someone is still typing. */
export function draftTotal(draft: Draft): number {
  return Object.values(draft).reduce((sum, raw) => {
    const n = Number.parseFloat(raw);
    return Number.isFinite(n) && n >= 0 ? sum + n : sum;
  }, 0);
}

export function draftToPayload(
  draft: Draft,
  campaigns: Array<{ campaign_id: string; campaign_name: string }>,
): Record<string, CampaignMonthlyBudget> {
  const byId = new Map(campaigns.map((c) => [c.campaign_id, c.campaign_name]));
  const out: Record<string, CampaignMonthlyBudget> = {};
  for (const [id, raw] of Object.entries(draft)) {
    if (raw.trim() === '') continue;
    const n = Number.parseFloat(raw);
    if (!Number.isFinite(n) || n < 0) continue;
    // The name travels with the budget so a delete-and-recreate (new id, same
    // name) is recoverable and a log line can say "Allergy".
    out[id] = { monthly: n, name: byId.get(id) ?? '' };
  }
  return out;
}

export function CampaignBudgetsCard({
  tenantId,
  campaigns,
  contractedMonthlyUsd,
  saved,
  onSaved,
}: CampaignBudgetsCardProps) {
  const [draft, setDraft] = useState<Draft>(() =>
    draftFromSaved(saved, campaigns),
  );

  // Re-seed when the client or the saved map changes. Without this, switching
  // tenants would show the previous client's budgets in the inputs.
  useEffect(() => {
    setDraft(draftFromSaved(saved, campaigns));
  }, [tenantId, saved, campaigns]);

  const total = useMemo(() => draftTotal(draft), [draft]);
  const cap = contractedMonthlyUsd ?? 0;
  const overCap = cap > 0 && total > cap;
  const pinnedCount = Object.values(draft).filter((v) => v.trim() !== '').length;

  const save = useMutation({
    mutationFn: () =>
      updateCampaignBudgets({
        tenantId,
        budgets: draftToPayload(draft, campaigns),
      }),
    onSuccess: (res) => {
      const n = Object.keys(res.campaign_monthly_budgets ?? {}).length;
      toast.success(
        n === 0
          ? 'Campaign budgets cleared. These campaigns go back to a share of the total.'
          : `${n} campaign ${n === 1 ? 'budget' : 'budgets'} saved. Pacing will move toward them over the next few runs.`,
      );
      onSaved?.();
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || 'Could not save those budgets.';
      toast.error(detail);
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PiggyBank className="h-5 w-5 text-coral" />
          Campaign budgets
        </CardTitle>
        <CardDescription>
          Give a campaign its own monthly budget and it stops being a share of
          the account total — it holds that figure even if the contract changes.
          Leave a row blank to keep it on the percentage split.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {campaigns.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No campaigns in pacing scope to budget.
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaign</TableHead>
                    <TableHead className="text-right">Monthly budget</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaigns.map((c) => {
                    const raw = draft[c.campaign_id] ?? '';
                    const pinned = raw.trim() !== '';
                    return (
                      <TableRow key={c.campaign_id}>
                        <TableCell>
                          <span className="font-medium">{c.campaign_name}</span>
                          {!pinned ? (
                            <Badge variant="outline" className="ml-2 text-[10px]">
                              share of total
                            </Badge>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex items-center justify-end gap-1">
                            <span className="text-xs text-muted-foreground">$</span>
                            <Input
                              aria-label={`${c.campaign_name} monthly budget`}
                              type="number"
                              min={0}
                              step={1}
                              placeholder="—"
                              value={raw}
                              onChange={(e) =>
                                setDraft({
                                  ...draft,
                                  [c.campaign_id]: e.target.value,
                                })
                              }
                              className="h-8 w-[7rem] text-right tabular-nums"
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
              <div className="text-sm">
                <span className="tabular-nums font-medium">{usd(total)}</span>
                <span className="text-muted-foreground">
                  {' '}
                  across {pinnedCount} of {campaigns.length} campaigns
                </span>
                {cap > 0 ? (
                  <span
                    className={
                      overCap
                        ? 'ml-2 text-destructive'
                        : 'ml-2 text-muted-foreground'
                    }
                  >
                    {overCap ? 'over' : 'of'} the {usd(cap)} contracted
                  </span>
                ) : null}
              </div>
              <Button
                onClick={() => save.mutate()}
                /* Blocked here as well as server-side: the server is the
                   authority, but letting the click through only to bounce it
                   teaches people to ignore the total. */
                disabled={save.isPending || overCap}
              >
                {save.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save budgets
              </Button>
            </div>

            {overCap ? (
              <p className="text-sm text-destructive">
                These budgets total more than the contracted monthly figure.
                Lower a campaign, or raise the contract first.
              </p>
            ) : null}

            <p className="text-xs text-muted-foreground">
              Budget changes move gradually — pacing shifts a campaign by at
              most 15% per run to avoid resetting Google&apos;s bidding, so a
              large change takes a few runs to land.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
